<template>
  <nav class="fixed w-full z-10 bg-transparent">
    <div
      v-show="!isMobile"
      class="flex justify-between items-center h-[60px] px-2 lg:px-4"
      :class="{ gradient }"
    >
      <div class="relative left-[-15px]">
        <LogoAnimated />
      </div>

      <div class="flex items-center gap-4">
        <VendorSettingsAiCreditsDropdown v-if="navigationType === 'vendor'" />
        <template v-for="item in navItems['regular']" :key="item.id">
          <BaseLink
            v-if="item.type === 'link'"
            :to="item.to ?? ''"
            class="nav-link hover-bg-custom whitespace-nowrap"
            :class="{ active: route.path === item.to }"
          >
            {{ item.label }}
          </BaseLink>
          <MenuDropdown
            v-if="item.type === 'dropdown'"
            :options="item.dropdown ?? []"
            :label="item.label"
          />
        </template>
        <NavigationButton :type="navigationType" />
      </div>
    </div>

    <div v-show="isMobile" class="relative">
      <div class="h-[60px] px-4 relative z-20">
        <div class="flex justify-between items-center h-full">
          <div class="relative left-[-15px]">
            <LogoAnimated />
          </div>
          <div class="flex justify-center items-center gap-1">
            <NavigationButton :type="navigationType" />
            <BaseButton
              @click="toggleMobileMenu"
              id="btn-mobile-menu"
              class="bg-primary py-[0.5rem] px-[0.7rem] lg:py-[0.83rem] lg:px-[1rem] text-sm lg:text-base rounded-sm text-dark1 hover:bg-secondary duration-300 sm:hidden ml-2"
              aria-label="Toggle Menu"
              data-click-outside-exclude
            >
              <svgo-menu />
            </BaseButton>
          </div>
        </div>
      </div>
      <Transition name="slide-down">
        <div
          v-if="isMenuOpen"
          v-click-outside="closeMobileMenu"
          class="fixed inset-0 z-10"
        >
          <div
            class="flex flex-col items-center pt-20 pb-8 px-4 space-y-6 bg-dark2"
          >
            <template v-for="item in navItems['mobile']" :key="item.id">
              <BaseLink
                :to="item.to ?? ''"
                class="mobile-nav-link"
                :class="{ active: route.path === item.to }"
                @click.prevent="handleMobileItemClick(item.to ?? '')"
              >
                {{ item.label }}
              </BaseLink>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </nav>
</template>

<script setup lang="ts">
  import type { NavItem } from '@/types';
  import { onMounted, onUnmounted, ref } from 'vue';

  interface Props {
    navItems: { regular: NavItem[]; mobile: NavItem[] };
    gradient?: boolean;
    navigationType: 'vendor' | 'base' | 'researcher';
  }

  withDefaults(defineProps<Props>(), {
    gradient: false
  });

  const route = useRoute();
  const isMenuOpen = ref(false);
  const isMobile = ref(false);
  const mobileBreakpoint = 768;

  const handleMobileItemClick = (to: string) => {
    const isExternal =
      /^(http|https):\/\//.test(to) || /^mailto:/.test(to) || /^tel:/.test(to);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isExternal || (isMobile && !isExternal)) {
      closeMobileMenu();
      isExternal ? window.open(to, '_blank') : navigateTo(to);
    }
  };

  const handleResize = () => {
    isMobile.value = window.innerWidth < mobileBreakpoint;
    if (!isMobile.value && isMenuOpen.value) {
      closeMobileMenu();
    }
  };

  const toggleMobileMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
    document.body.style.overflow = isMenuOpen.value ? 'hidden' : '';
  };

  const closeMobileMenu = () => {
    isMenuOpen.value = false;
    document.body.style.overflow = '';
  };

  onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
  });
</script>

<style scoped lang="scss">
  .gradient {
    background: radial-gradient(
      23% 250% at 0% 0%,
      rgba(36, 49, 0, 0.6) 0%,
      rgba(17, 18, 22, 0) 100%
    );
  }

  .nav-link {
    @apply text-white hover:text-primary duration-300 py-2 px-[1rem] text-md lg:text-base rounded-sm;

    &.active {
      @apply text-primary;
    }
  }

  .nav--hidden {
    transform: translateY(-100%);
    transition: transform 0.3s ease-out;
  }

  .mobile-nav-link {
    @apply text-xl text-white hover:text-primary transition-colors duration-300;

    &.active {
      @apply text-primary;
    }
  }

  .slide-down-enter-active {
    animation: slide-down-in 0.3s ease-out forwards;
  }

  .slide-down-leave-active {
    animation: slide-down-out 0.2s ease-in forwards;
  }

  @keyframes slide-down-in {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes slide-down-out {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(-100%);
    }
  }
</style>
